import type { ColumnFiltersState } from '@tanstack/react-table';
import { formatNumber } from 'utils/number';
import type { PackageLibraryParams } from './atoms';
import type {
  InterestType,
  PackageLibraryAggregations,
  PackageType,
  SummaryCardType,
} from './types';

// ─────────────────────────────────────
// Summary Tiles
// ─────────────────────────────────────

export interface SummaryTileConfig {
  title: string;
  aggregationKey: keyof PackageLibraryAggregations;
  cardType?: SummaryCardType;
  format: 'count' | 'delta';
  valueColor?: 'default' | 'green';
  tooltipText?: string;
}

export const SUMMARY_TILES: SummaryTileConfig[] = [
  {
    title: 'Total Packages',
    aggregationKey: 'totalPackages',
    format: 'count',
  },
  {
    title: 'Opportunity Packages',
    aggregationKey: 'nonInventoryPackages',
    cardType: 'opportunity',
    format: 'count',
    tooltipText: 'Packages that do not contain any inventory assets',
  },
  {
    title: 'Packages Containing Inventory',
    aggregationKey: 'containsInventoryAssets',
    cardType: 'inventory',
    format: 'count',
    tooltipText: 'Packages that contain one or more inventory assets',
  },
  {
    title: 'Created in Last 24 Hours',
    aggregationKey: 'createdLast24h',
    cardType: 'last24h',
    format: 'delta',
    tooltipText: 'Packages created in the 24 hours preceding now',
  },
  {
    title: 'Created in Last 7 Days',
    aggregationKey: 'createdLast7d',
    cardType: 'last7d',
    format: 'delta',
    tooltipText: 'Packages created in the 7 days preceding now',
  },
  {
    title: 'Created in Last Month',
    aggregationKey: 'createdLastMonth',
    cardType: 'lastMonth',
    format: 'delta',
    tooltipText: 'Packages created in the 30 days preceding now',
  },
];

export const DEFAULT_PARAMS: PackageLibraryParams = {
  page: 1,
  pageSize: 100,
  sortAttribute: 'createdAt',
  sortDirection: 'DESC',
};

export const PACKAGE_TYPE_LABELS: Record<PackageType, string> = {
  OpportunityPackage: 'Opportunity',
  AccountPackage: 'Inventory',
};

export const INTEREST_TYPE_LABELS: Record<InterestType, string> = {
  working: 'Working Interest',
  royalty: 'Royalty Interest',
};

export const SORT_FIELD_MAP: Record<string, string> = {
  name: 'name',
  sellerName: 'sellerName',
  acquirerName: 'acquirerName',
  type: 'type',
  interestType: 'interestType',
  closeDate: 'closeDate',
  totalAcreage: 'totalAcreage',
  containsInventoryAssets: 'containsInventoryAssets',
  effectiveDate: 'effectiveDate',
  includeChildren: 'includeChildren',
  completedEvals: 'completedEvals',
  workspaceCount: 'workspaceCount',
  createdAt: 'createdAt',
  updatedAt: 'updatedAt',
};

export const formatDateForInput = (
  date: Date | null | undefined
): string | null => {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const parseDateFromInput = (
  dateStr: string | null | undefined
): Date | undefined => {
  if (!dateStr) return undefined;
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
};

export const formatCount = (value: number | undefined): string => {
  if (value === undefined) return '--';
  return formatNumber(value, 0, 0, 'decimal', true, '--');
};

export const formatDelta = (value: number | undefined): string => {
  if (value === undefined) return '--';
  return `+${formatCount(value)}`;
};

export const formatInterestType = (raw: string | null | undefined): string => {
  if (!raw) return '--';
  return raw
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean)
    .map(
      (t) =>
        INTEREST_TYPE_LABELS[t as InterestType] ??
        `${t.charAt(0).toUpperCase()}${t.slice(1)}`
    )
    .join(', ');
};

const parseDateRange = (
  raw: unknown
): { start?: string; end?: string } | undefined => {
  if (!Array.isArray(raw)) return undefined;
  const [start, end] = raw as [string | null, string | null];
  if (!start && !end) return undefined;
  return {
    start: start ?? undefined,
    end: end ?? undefined,
  };
};

export const columnFiltersToParams = (
  filters: ColumnFiltersState
): Partial<PackageLibraryParams> => {
  const params: Partial<PackageLibraryParams> = {
    containsInventoryAssets: undefined,
    interestTypes: undefined,
    createdAtStart: undefined,
    createdAtEnd: undefined,
    updatedAtStart: undefined,
    updatedAtEnd: undefined,
  };

  for (const filter of filters) {
    switch (filter.id) {
      case 'containsInventoryAssets': {
        const values = Array.isArray(filter.value)
          ? (filter.value as string[])
          : [];
        params.containsInventoryAssets =
          values[0] === 'true' ? true : undefined;
        break;
      }
      case 'interestType':
        params.interestTypes = filter.value as string[];
        break;
      case 'createdAt': {
        const range = parseDateRange(filter.value);
        params.createdAtStart = range?.start;
        params.createdAtEnd = range?.end;
        break;
      }
      case 'updatedAt': {
        const range = parseDateRange(filter.value);
        params.updatedAtStart = range?.start;
        params.updatedAtEnd = range?.end;
        break;
      }
    }
  }

  return params;
};
